import React, { useState, useMemo, useCallback } from "react";
import { IconButton, Typography, Grid, Button } from "@mui/material/";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { groupBy } from "lodash";
import { strings } from "../../../../../common/i18n";
import { enums, format, helpers } from "../../../../../common/lib";
import actions from "../../../../../Store/actions";
import {
    InfoThemeProvider,
    ErrorThemeProvider,
    SuccessThemeProvider
} from "../../../../../common/styles/providers";
import {
    useAssetTypesForOrganisation,
    useMakesForAssetTypes,
    useAuditStatus,
    usePendingCountApproval
} from "../../../../../common/hooks";
import PendingCountApprovalDialog from "../../PendingCountApprovalDialog";
import {
    checkStatuses,
    customBooleanFilterOptions,
    getIdentifiers,
    renderCircle
} from "./tableHelper";
import AltAssetHelpers from "./useUnexpectedAssetColsRows";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import styles from "./styles";
import { logToSentry } from "../../../../../config/Sentry";
import ShareIcon from "@mui/icons-material/Share";
import { nonMandatoryReasonLabel } from "../../AuditAsset/LayoutComponents/NonMandatoryReason";

/**
 * =============================================
 * DATE FILTER UTILITIES WITH VALIDATION
 * =============================================
 */

/**
 * Hook for expected assets table and toolbar logic
 *
 * Key contract:
 * - The hook returns `expectedToolBar` which is a function: (selectedRows) => JSX
 *   The MAT table should call: toolBarComponents(selectedRowData)
 *
 * - The hook still exposes `handleExpectedChangeState(state)` which is used by the MRT wrapper
 *   to persist sorting/filter/selection if desired. The hook stores `expectedAssetSelection` there (IDs array).
 */

export default function useExpectedAssets() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [excludeDialogOpen, setExcludeDialogOpen] = useState(false);
    const [moveAssetDialogOpen, setMoveAssetDialogOpen] = useState(false);
    const [expectedLoading, setExpectedLoading] = useState(false);

    // Stored selection (IDs) — still kept for any other consumers or persisted needs.
    const [expectedAssetSelection, setExpectedAssetSelection] = useState([]);

    const auditStatus = useAuditStatus().id;
    const {
        guardApproval,
        guardReject,
        dialogProps: pendingCountDialogProps
    } = usePendingCountApproval();

    const {
        assets,
        auditAssets,
        auditReview,
        auditControllers,
        typesList,
        altAssets,
        assetFoundElsewhere,
        assetAuditors,
        auth,
        userOrganisations
    } = useSelector((state) => state);

    /** ----------------- Navigation / Row click ----------------- */
    const handleExpectedEdit = useCallback(
        (row) => {
            try {
                navigate(`${pathname}/assets/${row.id}`);
            } catch (error) {
                const errStr = error.toString();
                console.error(errStr);
                logToSentry(`On expected asset row click: ${errStr}`);
                dispatch(
                    actions.toast.create.error(strings("errors.onAssetClick"))
                );
            }
        },
        [navigate, pathname, dispatch]
    );

    /** ----------------- Helpers / derived data ----------------- */

    const isSoldDate = useMemo(() => {
        const assetIsSoldId = 2; // Asset is Sold Id
        return typesList.exclusionReasonTypes.data.find(
            (item) => item.id === assetIsSoldId
        );
    }, [typesList.exclusionReasonTypes]);

    const { assetIsSold, assetDeliveredToCustomerAndNotPaid } =
        enums.status.exclusionReason;
    const isExclusionDatePicker = [
        assetIsSold,
        assetDeliveredToCustomerAndNotPaid
    ];

    const auditAssetAuditors = useMemo(
        () => groupBy(assetAuditors.data, (item) => item.auditAsset),
        [assetAuditors]
    );

    const getControllerAsset = useCallback(
        (list, id) => list.find((item) => item.auditAsset === id),
        []
    );

    const auditAssetNotes = useMemo(() => {
        return assetAuditors.data.reduce((aggregate, item) => {
            (aggregate[item.auditAsset] ||= []).push(
                ...item.auditNotes.map((n) => n.content)
            );
            return aggregate;
        }, {});
    }, [assetAuditors]);

    const auditAssetExclusionReasons = useMemo(() => {
        return assetAuditors.data.reduce((aggregate, item) => {
            if (item.exclusionReason) {
                const { name } = helpers.getListItemForId(
                    typesList.exclusionReasonTypes,
                    item.exclusionReason
                );

                if (isExclusionDatePicker.includes(item.exclusionReason)) {
                    const exclusionDate = `${name} on ${format.date(
                        item.exclusionReasonDate
                    )}`;
                    (aggregate[item.auditAsset] ||= []).push({
                        name,
                        displayStr: exclusionDate
                    });
                } else
                    (aggregate[item.auditAsset] ||= []).push({
                        name,
                        displayStr: name
                    });
            }
            return aggregate;
        }, {});
    }, [assetAuditors, typesList, isExclusionDatePicker]);

    const getControllerExclusion = useCallback(
        (asset) => {
            if (asset && asset.exclusionReason) {
                const { name } = helpers.getListItemForId(
                    typesList.exclusionReasonTypes,
                    asset.exclusionReason
                );
                if (asset.exclusionReasonDate) {
                    return {
                        name,
                        displayStr: `${name} on ${format.date(
                            asset.exclusionReasonDate
                        )}`
                    };
                } else {
                    return { name, displayStr: name };
                }
            } else return "";
        },
        [typesList]
    );

    const getAsstFoundElseWhere = useCallback(
        (asset) => {
            const { unexpected } =
                assetFoundElsewhere && assetFoundElsewhere.data[0]
                    ? assetFoundElsewhere.data[0]
                    : {};
            const assetFound =
                unexpected && unexpected.map((item) => item.assetId);
            return assetFound && assetFound.includes(asset) ? "pass" : "fail";
        },
        [assetFoundElsewhere]
    );

    /** ----------------- Rows data (expectedData) ----------------- */
    const expectedData = useMemo(() => {
        let data;
        const statusType = checkStatuses(auditReview.status);
        if (statusType === "validatedVisibility") {
            data = auditReview.validatedVisibility;
        } else if (statusType === "expected" || statusType === "nonMandatory") {
            data = auditReview.auditAssets;
        } else {
            return [];
        }

        const { assetTypes, makes, assetStatus, categories, attributes } =
            typesList;
        const conditionAttributeId = attributes.data.find(
            (attribute) => attribute.id === enums.types.assetAttribute.condition
        )?.id;

        return data.ids
            .map((id) => {
                const auditAsset = helpers.getListItemForId(auditAssets, id);
                const controllerAsset = getControllerAsset(
                    auditControllers.data,
                    id
                );

                const asset = helpers.getListItemForId(
                    assets,
                    auditAsset.asset
                );
                const {
                    make,
                    type,
                    attributes: assetAttributes
                } = asset || {};
                const categoryId = asset?.auxiliary?.[0]?.category ?? null;
                const auditorExlusions = auditAssetExclusionReasons[id];
                const latestAuditorExclusion = auditorExlusions
                    ? auditorExlusions[auditorExlusions.length - 1]
                    : { name: "", displayStr: "" };

                const assetMake = helpers.getListItemForId(makes, make);
                const assetType = helpers.getListItemForId(assetTypes, type);
                const assetCategory = helpers.getListItemForId(
                    categories,
                    categoryId
                );
                const conditionEntry =
                    conditionAttributeId != null &&
                    Array.isArray(assetAttributes)
                        ? assetAttributes.find(
                              (a) => a.attribute === conditionAttributeId
                          )
                        : null;

                const {
                    isExpected,
                    reissuedStatus,
                    isMandatory,
                    isPreApproved,
                    isApproved,
                    assetRejection,
                    status
                } = auditAsset || {};

                const currentAssetStatus = helpers.getListItemForId(
                    assetStatus,
                    status
                );

                const getCurrentAssetStatusValue = (value) => {
                    const { id } = value || {};
                    if (id === 1) return "none";
                    if (id === 2) return "pass";
                    if (id === 3) return "fail";
                    return "none";
                };

                const genValue = (val) => {
                    if (val === true) return "pass";
                    if (val === false) return "fail";
                    if (val === null) return "none";
                };

                return {
                    id,
                    ...auditAsset,
                    ...getIdentifiers(asset),
                    auditorExlusionReason: latestAuditorExclusion.name,
                    assetActionStatus:
                        (isApproved === null && assetRejection.length === 0) ||
                        (isApproved === null &&
                            assetRejection.length > 0 &&
                            reissuedStatus === 1)
                            ? true
                            : false,
                    auditorExclusionDisplayString:
                        latestAuditorExclusion.displayStr,
                    controllerExclusion:
                        getControllerExclusion(controllerAsset).name,
                    controllerExclusionDisplayString:
                        getControllerExclusion(controllerAsset).displayStr,
                    make: assetMake?.name,
                    type: assetType?.name,
                    condition: conditionEntry?.value ?? null,
                    category: assetCategory?.name ?? null,
                    nonMandatoryReasonLabel: nonMandatoryReasonLabel(
                        auditAsset.nonMandatoryReason
                    ),
                    notes: (auditAssetNotes[id] || []).join(""),
                    autoVerified: genValue(isPreApproved),
                    expected: genValue(isExpected && isMandatory),
                    seen: getCurrentAssetStatusValue(currentAssetStatus),
                    foundElsewhere: `${getAsstFoundElseWhere(
                        auditAsset.asset
                    )}`,
                    // Add date fields for filtering if they exist in your data
                    exclusionReasonDate: auditAsset.exclusionReasonDate
                        ? new Date(auditAsset.exclusionReasonDate)
                        : null
                };
            })
            .sort((a, b) => {
                // keep assets with assetActionStatus === false first (as your old sort intended)
                if (a.assetActionStatus === b.assetActionStatus) return 0;
                return a.assetActionStatus ? -1 : 1;
            });
    }, [
        auditReview,
        auditAssets,
        auditControllers,
        assets,
        auditAssetExclusionReasons,
        auditAssetNotes,
        typesList,
        getControllerAsset,
        getControllerExclusion,
        getAsstFoundElseWhere
    ]);

    /** ----------------- Asset Types and makes (for filters) ----------------- */
    const assetTypes = useAssetTypesForOrganisation();
    const makes = useMakesForAssetTypes(assetTypes.map((item) => item.id));

    const makesFilter = useMemo(() => makes.map((item) => item.name), [makes]);
    const typesFilter = useMemo(
        () => assetTypes.map((item) => item.name),
        [assetTypes]
    );
    const exclusionReasonFilter = useMemo(
        () => typesList.exclusionReasonTypes.data.map((item) => item.name),
        [typesList]
    );
    const categoryFilter = useMemo(
        () => typesList.categories.data.map((item) => item.name),
        [typesList]
    );
    const conditionFilter = useMemo(() => {
        const conditionAttr = typesList.attributes?.data.find(
            (a) => a.name === "condition"
        );
        if (!conditionAttr) return [];
        const values = new Set(
            assets.data
                .flatMap((a) => a.attributes || [])
                .filter((a) => a.attribute === conditionAttr.id && a.value)
                .map((a) => a.value)
        );
        return Array.from(values);
    }, [typesList, assets]);

    /** ----------------- Helper functions that operate on selectedRows ----------------- */

    // Accepts selectedRows array (array of original row objects)
    const getSelectedAssetData = useCallback(
        (selectedRows) => {
            if (!selectedRows || selectedRows.length === 0) return [];
            const selectedIds = selectedRows.map(
                (r) => r.id ?? r.original?.id ?? r
            );

            // normalize to IDs and then map to full auditAssets items
            return selectedIds.map((item) =>
                helpers.getListItemForId(auditAssets, item)
            );
        },
        [auditAssets]
    );

    const selectedHasUnassigned = useCallback(
        (selectedRows) => {
            const selectedAssetData = getSelectedAssetData(selectedRows);
            return selectedAssetData.some((item) => item && !item.isAssigned);
        },
        [getSelectedAssetData]
    );

    const selectedCount = useCallback(
        (selectedRows) => (selectedRows ? selectedRows.length : 0),
        []
    );

    /** ----------------- Conditions used to enable/disable toolbar actions ----------
     *  We compute them on-demand from the selectedRows that MAT passes into our toolbar factory.
     *  This ensures hook controls the decision logic entirely.
     */

    // actionButtonsCondition -> true means disabled (matching your original semantics)
    const computeActionButtonsCondition = useCallback(
        (selectedRows, actionType) => {
            if (expectedLoading) return true;
            const count = selectedCount(selectedRows);
            if (count === 0) return true;

            // If any selected asset is unchecked (not assigned) -> disable
            if (actionType !== "moveAsset")
                if (selectedHasUnassigned(selectedRows)) return true;

            // else allow action
            return false;
        },
        [expectedLoading, selectedCount, selectedHasUnassigned]
    );

    const computeIsUncheckedAsset = useCallback(
        (selectedRows) => {
            // your previous isUncheckedAsset logic: if any selected non-outstanding assets present, return true;
            // but original logic is ambiguous — I'll reflect intent: disabled if selected has assigned & expected? keep earlier logic where selection of assigned expected leads to disabled true
            if (expectedLoading) return true;
            const selectedAssetData = getSelectedAssetData(selectedRows);
            const selectedNonOutstandingAssets = selectedAssetData.filter(
                (item) => item && item.isAssigned && item.isExpected
            );
            const uncheckedAssets = auditAssets.data.filter(
                (item) => !item.isAssigned && item.isExpected
            );
            if (selectedNonOutstandingAssets.length > 0) return true;
            if (uncheckedAssets && uncheckedAssets.length > 0) return false;
            // fallback
            return false;
        },
        [expectedLoading, getSelectedAssetData, auditAssets]
    );

    const showMoveAssetButtonCondition = useMemo(() => {
        const {
            pendingCount,
            pendingReview,
            inReview,
            partiallyCompleteCount
        } = enums.status.audit;

        return (
            userOrganisations?.item?.id ===
                enums.organisationIds.stellantisFinanceFrance &&
            (auditStatus === pendingReview ||
                // auditStatus === pendingCount ||
                auditStatus === inReview ||
                auditStatus === partiallyCompleteCount)
        );
    }, [userOrganisations, enums, auditStatus]);

    /** ----------------- toolbar factory (exposed) -----------------
     * expectedToolBar is a function that takes selectedRows (array of original row objects)
     * and returns the toolbar JSX. The MAT wrapper should call `expectedToolBar(selectedRows)`.
     */
    const expectedToolBar = useCallback(
        (selectedRows) => {
            // compute derived values
            const selectedAssetData = getSelectedAssetData(selectedRows);
            const numSelected = selectedAssetData.length;
            const isUnchecked = computeIsUncheckedAsset(selectedRows);
            const actionButtonsDisabled =
                computeActionButtonsCondition(selectedRows);
            // Moving asset button shown only for Stellantis org
            const canMoveAssetButton = computeActionButtonsCondition(
                selectedRows,
                "moveAsset"
            );
            // const &&
            // showMoveAssetButtonCondition;

            // we keep the same providers and styles you previously used
            return (
                <>
                    <PendingCountApprovalDialog {...pendingCountDialogProps} />
                    {/** Exclude button (appears only when canExcludeAsset is true) */}
                    {(() => {
                        // compute canExcludeAsset from audit status & unchecked assets (same as original)
                        const {
                            awaitingDownload,
                            pendingCount,
                            partiallyCompleteCount
                        } = enums.status.audit;
                        const uncheckedAssets = auditAssets.data.filter(
                            (item) => !item.isAssigned && item.isExpected
                        );
                        const canExclude = [
                            awaitingDownload,
                            pendingCount,
                            partiallyCompleteCount
                        ].includes(auditStatus);
                        if (!canExclude) return null;
                        return (
                            <InfoThemeProvider key="exclude">
                                <Button
                                    sx={styles.toolBarButtons}
                                    variant="contained"
                                    onClick={() =>
                                        onExcludeDialogOpen(selectedAssetData)
                                    }
                                    disabled={isUnchecked}
                                >
                                    {strings("exclude")}
                                </Button>
                            </InfoThemeProvider>
                        );
                    })()}

                    {/** Reject / Approve buttons (disabled/enabled according to computed rules) */}
                    {!(() => {
                        const { awaitingDownload, pendingCount } =
                            enums.status.audit;
                        const isReadOnly = [awaitingDownload].includes(
                            auditStatus
                        );
                        return isReadOnly;
                    })() && (
                        <>
                            {showMoveAssetButtonCondition && (
                                <InfoThemeProvider key="exclude">
                                    <Button
                                        sx={styles.toolBarButtons}
                                        variant="contained"
                                        onClick={() =>
                                            onMoveAssetDialogOpen(
                                                selectedAssetData
                                            )
                                        }
                                        disabled={canMoveAssetButton}
                                    >
                                        {strings("moveAsset")}
                                    </Button>
                                </InfoThemeProvider>
                            )}
                            <ErrorThemeProvider>
                                <Button
                                    sx={styles.toolBarButtons}
                                    color="primary"
                                    onClick={() =>
                                        onRejectDialogShow(selectedAssetData)
                                    }
                                    disabled={actionButtonsDisabled}
                                    variant="contained"
                                >
                                    {strings("reject")}
                                </Button>
                            </ErrorThemeProvider>

                            <SuccessThemeProvider>
                                <Button
                                    sx={styles.toolBarButtons}
                                    color="primary"
                                    disabled={actionButtonsDisabled}
                                    onClick={() => onApprove(selectedAssetData)}
                                    variant="contained"
                                >
                                    {strings("approve")}
                                </Button>
                            </SuccessThemeProvider>
                        </>
                    )}
                </>
            );
        },
        [
            getSelectedAssetData,
            computeActionButtonsCondition,
            computeIsUncheckedAsset,
            showMoveAssetButtonCondition,
            expectedLoading,
            auditAssets,
            auditStatus,
            styles,
            pendingCountDialogProps
        ]
    );

    /** Toolbar Component */
    const expectedRowActions = (row) => {
        const { branchName } = row.original;
        return [
            <IconButton
                key="edit"
                onClick={() => handleExpectedEdit(row.original)}
            >
                <VisibilityOutlinedIcon color="primary" />
            </IconButton>
        ];
    };

    /** ----------------- Handler: table state changes (called by MRT wrapper) -----------
     * This will update expectedAssetSelection (IDs) to preserve your previous behaviour.
     * Expects state param shape similar to MRT onStateChange - we guard access.
     */
    const handleExpectedChangeState = useCallback(
        (state) => {
            try {
                // MRT state shape: state.rowSelection (object), state.selection? older ... be defensive
                // You previously destructured sorting, filter, selection
                const { selection } = state || {};
                // selection may be an object keyed by id or array; we will normalize to array of IDs
                let normalizedSelection = [];

                // MRT getSelectionModel returns object { id: true } (if using table.getSelectedRowModel)
                // but you also previously used selection array — handle both
                if (Array.isArray(selection)) {
                    normalizedSelection = selection;
                } else if (selection && typeof selection === "object") {
                    normalizedSelection = Object.keys(selection).filter(
                        (k) => selection[k]
                    );
                } else if (
                    state?.rowSelection &&
                    typeof state.rowSelection === "object"
                ) {
                    normalizedSelection = Object.keys(
                        state.rowSelection
                    ).filter((k) => state.rowSelection[k]);
                }

                setExpectedAssetSelection(normalizedSelection);
            } catch (error) {
                const errStr = error.toString();
                console.error(errStr);
                logToSentry(`On audit table state change: ${errStr}`);
                dispatch(
                    actions.toast.create.error(
                        strings("errors.onAuditTableStateChange")
                    )
                );
            }
        },
        [dispatch]
    );

    /** ----------------- Actions that operate on selected rows ----------------- */
    // onApprove: expects to operate on currently selected rows; we accept optional selectedAssetData param for direct call.
    const approveSelected = useCallback(
        async (selectedAssetDataParam) => {
            try {
                setExpectedLoading(true);

                // if function called without param, fall back to expectedAssetSelection stored IDs
                let selectedAssetData;
                if (Array.isArray(selectedAssetDataParam)) {
                    selectedAssetData = selectedAssetDataParam;
                } else {
                    selectedAssetData = expectedAssetSelection;
                }

                const next = selectedAssetData.map((item) => ({
                    id: item.id,
                    isApproved: true,
                    isAssigned: item.isAssigned,
                    reissuedStatus: item.reissuedStatus
                        ? enums.status.reissued.resolved
                        : null,
                    updatedBy: auth.account.id
                }));

                // Report the count the API confirms, not the count we sent.
                // BulkUpdateAuditAssetViewSet runs the whole batch inside one
                // transaction.atomic() and returns a row per asset it actually
                // updated, so a mismatch here means something was dropped and
                // the user should not be told "approved — 300".
                const result = await dispatch(
                    actions.auditAssets.create.patchRequest(next)
                );
                const confirmed = Array.isArray(result?.auditAssets)
                    ? result.auditAssets.length
                    : next.length;

                if (confirmed < next.length) {
                    dispatch(
                        actions.toast.create.warning(
                            strings("errors.onApprovePartial", {
                                confirmed,
                                requested: next.length
                            })
                        )
                    );
                } else {
                    dispatch(
                        actions.toast.create.success(
                            `${strings("approved")} — ${confirmed}`
                        )
                    );
                }
            } catch (error) {
                const errStr = error.toString();
                console.error(errStr);
                logToSentry(`On approve asset: ${errStr}`);
                dispatch(
                    actions.toast.create.error(strings("errors.onApproveAsset"))
                );
            } finally {
                setExpectedLoading(false);
            }
        },
        [expectedAssetSelection, auditAssets, auth, dispatch]
    );

    /* Bulk approve is the path most likely used to clear a whole audit at once,
     * so it gets the same disclaimer as approving a single asset. Outside
     * Pending Count guardApproval just runs the handler. */
    const onApprove = useCallback(
        (selectedAssetDataParam) =>
            guardApproval(() => approveSelected(selectedAssetDataParam)),
        [guardApproval, approveSelected]
    );

    const onExcludeDialogOpen = useCallback(
        (selectedRows) => {
            setExpectedAssetSelection(selectedRows);
            setExcludeDialogOpen(true);
        },
        [expectedAssetSelection]
    );

    const onMoveAssetDialogOpen = useCallback(
        async (selectedRows) => {
            try {
                setExpectedAssetSelection(selectedRows);
                setMoveAssetDialogOpen(true);
            } catch (error) {
                const errStr = error.toString();
                console.error(errStr);
                logToSentry("On moving asset:", { error });
                dispatch(
                    actions.toast.create.error(strings("errors.onMovingAsset"))
                );
            } finally {
                setExpectedLoading(false);
            }
        },
        [expectedAssetSelection]
    );

    const onExcludeDialogClose = useCallback(
        () => setExcludeDialogOpen(false),
        []
    );

    const onRejectDialogHide = useCallback(
        () => setRejectDialogOpen(false),
        []
    );
    const onRejectDialogShow = useCallback(
        (selectedRows) =>
            guardReject(() => {
                setExpectedAssetSelection(selectedRows);
                setRejectDialogOpen(true);
            }),
        [guardReject, expectedAssetSelection]
    );

    const onMoveAssetDialogClose = useCallback(
        () => setMoveAssetDialogOpen(false),
        []
    );

    const onRejectSubmit = useCallback(
        async (reissuedStatus, rejectionReason) => {
            try {
                setExpectedLoading(true);

                const selectedAssetData = expectedAssetSelection.map((item) =>
                    helpers.getListItemForId(auditAssets, item)
                );

                const next = expectedAssetSelection.map((item) => ({
                    id: item.id,
                    status: reissuedStatus ? 1 : item.status,
                    isApproved: false,
                    isAssigned: item.isAssigned,
                    reissuedStatus,
                    rejectionReason,
                    updatedBy: auth.account.id
                }));

                await dispatch(actions.auditAssets.create.patchRequest(next));
                dispatch(
                    actions.toast.create.info(
                        `${strings("rejected")} — ${next.length}`
                    )
                );
                setRejectDialogOpen(false);
            } catch (error) {
                const errStr = error.toString();
                console.error(errStr);
                logToSentry(`On reject asset: ${errStr}`);
                dispatch(
                    actions.toast.create.error(
                        strings("errors.onRejectingAsset")
                    )
                );
            } finally {
                setExpectedLoading(false);
            }
        },
        [expectedAssetSelection, auditAssets, auth, dispatch]
    );

    const expectedRowClassName = useCallback((params) => {
        const { assetActionStatus } = params.row || params;
        return !assetActionStatus ? "bold-row" : "normal-row";
    }, []);

    /** ----------------- Enhanced Columns with Proper Filtering ----------------- */
    /** ----------------- Enhanced Columns with Proper Filtering and Copying ----------------- */
    const isNonMandatoryView =
        checkStatuses(auditReview.status) === "nonMandatory";

    // Only shown on the Non-mandatory list, where every row should have a reason
    const nonMandatoryReasonColumn = useMemo(
        () => ({
            id: "nonMandatoryReason",
            header: strings("reason"),
            accessorKey: "nonMandatoryReasonLabel",
            filterVariant: "autocomplete",
            filterSelectOptions: Object.values(
                strings("messages.nonMandatoryReasons", { returnObjects: true })
            ),
            size: 200,
            Cell: ({ cell }) => cell.getValue() ?? "–"
        }),
        []
    );

    const expectedColumns = useMemo(() => {
        // Build columns with proper filtering
        const columns = [
            {
                id: "identifier1",
                header: strings("identifier1"),
                accessorKey: "identifier1",
                hideable: false,
                filterVariant: "autocomplete",
                enableSorting: false,
                size: 150,
                Cell: ({ row }) => (
                    <Grid container wrap="nowrap" spacing={2}>
                        {row.original.sharedValidationAsset && (
                            <Grid item>
                                <ShareIcon color="info" />
                            </Grid>
                        )}
                        {row.original.identifiersList && (
                            <Grid item xs>
                                {row.original.identifiersList.map(
                                    (item, index) =>
                                        item ? (
                                            <Typography
                                                display="block"
                                                variant="caption"
                                                key={`${row.index}-${index}`}
                                            >
                                                {item}
                                            </Typography>
                                        ) : null
                                )}
                            </Grid>
                        )}
                    </Grid>
                ),
                // Ensure proper copying by providing the actual data value
                accessorFn: (originalRow) => {
                    // Return the actual identifier value for copying
                    return (
                        originalRow.identifier1 ||
                        originalRow.identifiersList?.join(", ") ||
                        ""
                    );
                }
            },
            {
                id: "make",
                header: strings("make"),
                accessorKey: "make",
                filterVariant: "autocomplete",
                enableSorting: false,
                filterSelectOptions: makesFilter,
                size: 120,
                Cell: ({ cell }) => cell.getValue() || "-"
            },
            {
                id: "condition",
                header: strings("condition"),
                accessorKey: "condition",
                filterVariant: "autocomplete",
                filterSelectOptions: conditionFilter,
                size: 100,
                Cell: ({ cell }) => cell.getValue() ?? "–"
            },
            {
                id: "category",
                header: strings("category"),
                accessorKey: "category",
                filterVariant: "autocomplete",
                filterSelectOptions: categoryFilter,
                size: 100,
                Cell: ({ cell }) => cell.getValue() ?? "–"
            },
            {
                id: "auditorExclusion",
                header: strings("auditorExclusion"),
                accessorKey: "auditorExclusionDisplayString", // Use the display string for copying
                filterVariant: "autocomplete",
                filterSelectOptions: exclusionReasonFilter,
                size: 200,
                Cell: ({ cell }) => (
                    <Typography variant="caption">
                        {cell.getValue() || "-"}
                    </Typography>
                ),
                // Alternative: Use accessorFn to ensure proper value
                accessorFn: (originalRow) =>
                    originalRow.auditorExclusionDisplayString || ""
            },
            {
                id: "controllerExclusion",
                header: strings("controllerExclusion"),
                accessorKey: "controllerExclusionDisplayString", // Use the display string for copying
                filterVariant: "autocomplete",
                filterSelectOptions: exclusionReasonFilter,
                size: 250,
                Cell: ({ cell }) => (
                    <Typography variant="caption">
                        {cell.getValue() || "-"}
                    </Typography>
                ),
                // Alternative: Use accessorFn to ensure proper value
                accessorFn: (originalRow) =>
                    originalRow.controllerExclusionDisplayString || ""
            },
            {
                id: "notes",
                header: strings("note"),
                accessorKey: "notes",
                filterVariant: "text",
                enableSorting: false,
                filterFn: "contains",
                size: 200,
                Cell: ({ cell }) => {
                    const notes = cell.getValue();
                    return notes ? (
                        <Typography variant="caption" noWrap title={notes}>
                            {notes.length > 50
                                ? `${notes.substring(0, 50)}...`
                                : notes}
                        </Typography>
                    ) : (
                        "-"
                    );
                }
            },
            {
                id: "autoVerified",
                header: strings("autoverified"),
                accessorKey: "autoVerified",
                hideable: false,
                align: "center",
                size: 100,
                filterVariant: "autocomplete",
                filterSelectOptions: customBooleanFilterOptions,
                Cell: ({ row }) => renderCircle(row.original.autoVerified)
            },
            {
                id: "seen",
                header: strings("seen"),
                accessorKey: "seen",
                hideable: false,
                align: "center",
                size: 100,
                filterVariant: "autocomplete",
                filterSelectOptions: customBooleanFilterOptions,
                Cell: ({ row }) => renderCircle(row.original.seen)
            },
            {
                id: "expected",
                header: strings("expected"),
                accessorKey: "expected",
                hideable: true,
                hide: true,
                align: "center",
                size: 100,
                filterVariant: "autocomplete",
                filterSelectOptions: customBooleanFilterOptions,
                Cell: ({ row }) => renderCircle(row.original.expected)
            },
            {
                id: "foundElsewhere",
                header: strings("foundElsewhere"),
                filterVariant: "autocomplete",
                accessorKey: "foundElsewhere",
                filterSelectOptions: customBooleanFilterOptions,
                size: 120,
                align: "center",
                Cell: ({ row }) => renderCircle(row.original.foundElsewhere)
            },
            {
                id: "type",
                header: strings("type"),
                accessorKey: "type",
                filterVariant: "autocomplete",
                filterSelectOptions: typesFilter,
                size: 120,
                Cell: ({ cell }) => cell.getValue() ?? "–"
            }
        ];
        if (isNonMandatoryView) {
            // right after the identifier, so the reason is visible without scrolling
            columns.splice(1, 0, nonMandatoryReasonColumn);
        }
        return columns;
    }, [
        makesFilter,
        typesFilter,
        exclusionReasonFilter,
        conditionFilter,
        categoryFilter,
        isNonMandatoryView,
        nonMandatoryReasonColumn
    ]);

    /** ----------------- Expose hook API ----------------- */
    return {
        setExpectedLoading,
        expectedLoading,
        expectedColumns,
        expectedData,
        expectedRowClassName,
        expectedRowActions,
        rejectDialogOpen,
        excludeDialogOpen,
        moveAssetDialogOpen,
        handleExpectedEdit,
        handleExpectedChangeState,
        moveAssetDialogOpen,
        onMoveAssetDialogClose,
        onExcludeDialogOpen,
        onExcludeDialogClose,
        onRejectDialogHide,
        onRejectDialogShow,
        onRejectSubmit,
        expectedToolBar,
        expectedAssetSelection
    };
}
